import {DatePicker as ArkDatePicker} from '@ark-ui/react/date-picker'
import moment from 'moment'
import {
	ACCEPTED_INPUT_DATE_FORMATS,
	FALLBACK_DATE_LOCALE,
	fromArkDateValues,
	toArkDateValue,
	toArkDateValues,
} from '~/components/date_based_pickers/helpers'
import {useT} from '@eitje/i18n'
import './index.less'

/** @typedef {import('moment').Moment} Moment */

/**
 * @typedef {Object} DatePickerRootProps
 * @property {import('react').ReactNode} children Date-picker controls and views rendered inside the Ark root.
 * @property {string} [className] Class name applied to the Ark date-picker root.
 * @property {Moment[] | null} [value] Controlled selected dates; null represents no selection.
 * @property {'single' | 'multiple' | 'range'} [mode] Determines whether one date, multiple dates, or a date range can be selected.
 * @property {number} [numOfMonths] Number of months displayed at once. Defaults to one.
 * @property {'day' | 'month' | 'year'} [defaultPickerView] View shown when the picker opens.
 * @property {'day' | 'month' | 'year'} [minPickerView] Most detailed view available in the picker.
 * @property {(value: Moment[]) => void} [onChange] Called with every selected date converted to a Moment.
 * @property {Moment} [defaultFocusedValue] Initial date focused when the picker opens.
 * @property {Moment} [minDate] Earliest selectable date, inclusively.
 * @property {Moment} [maxDate] Latest selectable date, inclusively.
 * @property {(open: boolean) => void} [onOpenChange] Reports whether the picker is open.
 * @property {(date: Moment) => boolean} [disabledDateFn] Returns whether a date cannot be selected.
 * @property {boolean} [disabled] Whether the date picker is disabled.
 * @property {boolean} [readOnly] Whether the date picker is read-only.
 */

/** @param {DatePickerRootProps} props */
export const DatePickerRoot = ({
	children,
	className,
	value,
	mode = 'single',
	numOfMonths = 1,
	defaultFocusedValue,
	minDate,
	maxDate,
	defaultPickerView,
	minPickerView,
	onChange,
	onOpenChange,
	disabledDateFn,
	disabled,
	readOnly,
}) => {
	const {
		i18n: {language},
	} = useT()
	const locale = language || FALLBACK_DATE_LOCALE
	const selectedValue = value === undefined ? undefined : toArkDateValues(value)

	const convertInputValueToArkDateValue = value => {
		const date = moment(value, ACCEPTED_INPUT_DATE_FORMATS, true)

		return date.isValid() ? toArkDateValue(date) : undefined
	}

	// Use `isDateUnavailable` prop instead of `minDate/maxDate` for situations where the (default) value is outside the range
	// This way, Ark would still render the value correctly without snapping it into the first date of the range
	// e.g. value: 2026-02-01, minDate: 2026-02-02 -> displayed value: "2026-02-01"
	const isDateUnavailable = dateValue => {
		const date = fromArkDateValues([dateValue])[0]

		const isOutOfRange = (minDate && date.isBefore(minDate, 'day')) || (maxDate && date.isAfter(maxDate, 'day'))
		const isDisabledExplicitly = disabledDateFn ? disabledDateFn(date) : false

		return isOutOfRange || isDisabledExplicitly
	}

	const handleValueChange = details => {
		const {value} = details

		onChange?.(fromArkDateValues(value))
	}

	// Not to control the open state, but certain forms subscribes to the state change
	const handleOpenChange = details => {
		onOpenChange?.(details.open)
	}

	return (
		<ArkDatePicker.Root
			className={className}
			locale={locale}
			startOfWeek={1}
			value={selectedValue}
			defaultView={defaultPickerView}
			minView={minPickerView}
			defaultFocusedValue={toArkDateValue(defaultFocusedValue)}
			selectionMode={mode}
			numOfMonths={numOfMonths}
			onValueChange={handleValueChange}
			onOpenChange={handleOpenChange}
			isDateUnavailable={isDateUnavailable}
			parse={convertInputValueToArkDateValue}
			disabled={disabled}
			readOnly={readOnly}
			closeOnSelect={mode !== 'multiple'}
			positioning={{placement: 'bottom-start', overlap: true}}
			fixedWeeks // Prevents CLS when navigating between months
			outsideDaySelectable={numOfMonths === 1}
			openOnClick
			lazyMount
			unmountOnExit
		>
			{children}
		</ArkDatePicker.Root>
	)
}
